import {
  getInitialLocale,
  setDocumentLocale,
  translate,
  type Locale,
  type MessageKey,
  type TranslationParams,
} from '../i18n';
import { exportReportCsv, exportReportJson } from '../core/report-exporter';
import { analyzeFile, trimFile } from '../workers/analyzer-client';
import { bindDetailPanel, renderDetailPanel, type DetailPanelState } from './detail-panel';
import { bindDropZone, renderDropZone } from './drop-zone';
import { bindResultTable, renderResultTable } from './result-table';
import { bindToasts, renderToasts } from './toast';
import type {
  PreviewBackground,
  ResultFilter,
  ResultSort,
  ToastMessage,
  ToastTone,
  UiRecord,
  ZoomMode,
} from './types';
import {
  extractBlob,
  fileFingerprint,
  isPngFile,
  normalizeAnalysis,
  summarize,
  triggerDownload,
  trimmedFileName,
} from './utils';

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const MAX_BATCH_SIZE = 100;
const ANALYSIS_CONCURRENCY = 2;
const DEFAULT_REPOSITORY_URL = 'https://github.com/KanadeK/true-alpha';
const REPOSITORY_URL = trustedRepositoryUrl(import.meta.env.VITE_REPOSITORY_URL);

type Theme = 'light' | 'dark';

export class TrueAlphaApp {
  private readonly root: HTMLElement;
  private locale: Locale = getInitialLocale();
  private theme: Theme;
  private records: UiRecord[] = [];
  private selectedIds = new Set<string>();
  private activeId?: string;
  private search = '';
  private filter: ResultFilter = 'all';
  private sort: ResultSort = 'added';
  private toasts: ToastMessage[] = [];
  private nextToastId = 1;
  private analysisQueue: string[] = [];
  private activeAnalyses = 0;
  private renderFrame?: number;
  private detailCleanup: () => void = () => undefined;
  private trimmingId?: string;
  private detailState: DetailPanelState = {
    preview: {
      background: 'checker',
      zoomMode: 'fit',
      customScale: 1,
      showBoundingBox: false,
      showMarginGuides: false,
    },
    trimMargin: 0,
    trimming: false,
  };

  public constructor(root: HTMLElement) {
    this.root = root;
    this.theme = this.getInitialTheme();
    this.applyTheme();
    setDocumentLocale(this.locale);
    this.render();
  }

  public destroy(): void {
    this.detailCleanup();
    if (this.renderFrame !== undefined) cancelAnimationFrame(this.renderFrame);
    for (const record of this.records) URL.revokeObjectURL(record.objectUrl);
  }

  private readonly t = (key: MessageKey, params?: TranslationParams): string =>
    translate(this.locale, key, params);

  private getInitialTheme(): Theme {
    const saved = localStorage.getItem('truealpha-theme');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private applyTheme(): void {
    document.documentElement.dataset.theme = this.theme;
    document.documentElement.style.colorScheme = this.theme;
    document
      .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
      ?.setAttribute('content', this.theme === 'dark' ? '#101411' : '#f4f6f4');
  }

  private toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    localStorage.setItem('truealpha-theme', this.theme);
    this.applyTheme();
    this.render();
  }

  private toggleLocale(): void {
    this.locale = this.locale === 'en' ? 'zh-CN' : 'en';
    setDocumentLocale(this.locale);
    this.render();
  }

  private render(): void {
    const focusedElement = document.activeElement as HTMLInputElement | null;
    const restoreSearchFocus = focusedElement?.id === 'result-search';
    const selectionStart = restoreSearchFocus ? focusedElement.selectionStart : null;
    this.detailCleanup();

    const summary = summarize(this.records);
    const visibleRecords = this.getVisibleRecords();
    const activeRecord = this.records.find((record) => record.id === this.activeId);
    this.detailState.trimming = Boolean(activeRecord && activeRecord.id === this.trimmingId);

    this.root.innerHTML = `
      <div class="app-shell" data-testid="app">
        ${this.renderHeader()}
        <main class="workspace">
          <section class="intro" aria-labelledby="page-title">
            <div>
              <h1 id="page-title">${this.t('title')}</h1>
              <p>${this.t('subtitle')}</p>
            </div>
            <span class="local-badge"><span aria-hidden="true">✓</span>${this.t('privacyShort')}</span>
          </section>

          ${renderDropZone({ translate: this.t, disabled: this.records.length >= MAX_BATCH_SIZE, onFiles: (files) => this.addFiles(files) })}
          ${this.renderSummary(summary)}
          ${renderResultTable(
            {
              records: this.records,
              visibleRecords,
              selectedIds: this.selectedIds,
              activeId: this.activeId,
              search: this.search,
              filter: this.filter,
              sort: this.sort,
            },
            this.resultTableOptions(),
          )}
          ${renderDetailPanel(activeRecord, this.detailState, this.detailPanelOptions())}
          ${this.renderDetectionGuide()}
        </main>
        ${this.renderFooter()}
        ${renderToasts(this.toasts, { translate: this.t, onDismiss: (id) => this.dismissToast(id) })}
      </div>
    `;

    this.bind();
    if (restoreSearchFocus) {
      const search = this.root.querySelector<HTMLInputElement>('#result-search');
      search?.focus();
      if (selectionStart !== null) search?.setSelectionRange(selectionStart, selectionStart);
    }
  }

  private renderHeader(): string {
    const nextThemeLabel = this.theme === 'dark' ? this.t('lightTheme') : this.t('darkTheme');
    const languageLabel =
      this.locale === 'en' ? this.t('switchToChinese') : this.t('switchToEnglish');
    return `
      <header class="topbar">
        <div class="topbar__inner">
          <a class="brand" href="./" aria-label="${this.t('appName')}" data-testid="brand-link">
            <img src="${import.meta.env.BASE_URL}logo.svg" width="32" height="32" alt="" />
            <span>${this.t('appName')}</span>
          </a>
          <nav class="topbar__actions" aria-label="${this.t('primaryNavigation')}">
            <a class="nav-link" href="${REPOSITORY_URL}" target="_blank" rel="noreferrer" data-testid="github-link">
              <span aria-hidden="true">⌘</span>${this.t('github')}
            </a>
            <button class="nav-link" type="button" data-action="locale" aria-label="${languageLabel}" data-testid="language-toggle">
              <span aria-hidden="true">文</span>${this.locale === 'en' ? this.t('languageChinese') : this.t('languageEnglish')}
            </button>
            <button class="nav-link" type="button" data-action="theme" aria-label="${nextThemeLabel}" title="${nextThemeLabel}" data-testid="theme-toggle">
              <span aria-hidden="true">${this.theme === 'dark' ? '☀' : '◐'}</span>${this.theme === 'dark' ? this.t('lightTheme') : this.t('darkTheme')}
            </button>
          </nav>
        </div>
      </header>
    `;
  }

  private renderSummary(summary: ReturnType<typeof summarize>): string {
    const items: Array<{
      key: string;
      label: MessageKey;
      value: number;
      icon: string;
      tone: string;
    }> = [
      { key: 'total', label: 'summaryTotal', value: summary.total, icon: '▦', tone: 'neutral' },
      {
        key: 'analyzed',
        label: 'summaryAnalyzed',
        value: summary.analyzed,
        icon: '✓',
        tone: 'neutral',
      },
      {
        key: 'transparent',
        label: 'summaryTransparent',
        value: summary.transparent,
        icon: 'α',
        tone: 'success',
      },
      { key: 'opaque', label: 'summaryOpaque', value: summary.opaque, icon: '■', tone: 'neutral' },
      {
        key: 'suspected',
        label: 'summarySuspected',
        value: summary.suspected,
        icon: '!',
        tone: 'warning',
      },
      { key: 'errors', label: 'summaryErrors', value: summary.errors, icon: '×', tone: 'error' },
      {
        key: 'progress',
        label: 'summaryInProgress',
        value: summary.inProgress,
        icon: '↻',
        tone: 'working',
      },
    ];
    return `
      <section class="summary-panel" aria-labelledby="summary-title" data-testid="batch-summary">
        <h2 id="summary-title" class="visually-hidden">${this.t('batchSummary')}</h2>
        ${items
          .map(
            (item) => `
              <div class="summary-item summary-item--${item.tone}" data-testid="summary-${item.key}">
                <span class="summary-item__icon" aria-hidden="true">${item.icon}</span>
                <span class="summary-item__label">${this.t(item.label)}</span>
                <strong class="mono-value">${item.value}</strong>
              </div>
            `,
          )
          .join('')}
      </section>
    `;
  }

  private renderDetectionGuide(): string {
    const guides: Array<{ title: MessageKey; body: MessageKey; icon: string }> = [
      { title: 'guideRealTitle', body: 'guideRealBody', icon: 'α' },
      { title: 'guideUnusedTitle', body: 'guideUnusedBody', icon: '○' },
      { title: 'guideSolidTitle', body: 'guideSolidBody', icon: '!' },
      { title: 'guideCheckerTitle', body: 'guideCheckerBody', icon: '▦' },
      { title: 'guidePaddingTitle', body: 'guidePaddingBody', icon: '⌗' },
    ];
    return `
      <section class="guide-section" aria-labelledby="guide-title">
        <div class="section-heading"><h2 id="guide-title">${this.t('detectionGuide')}</h2></div>
        <div class="guide-grid">
          ${guides
            .map(
              (guide) => `
                <article class="guide-item">
                  <span class="guide-item__icon" aria-hidden="true">${guide.icon}</span>
                  <div><h3>${this.t(guide.title)}</h3><p>${this.t(guide.body)}</p></div>
                </article>
              `,
            )
            .join('')}
        </div>
      </section>
    `;
  }

  private renderFooter(): string {
    return `
      <footer class="footer">
        <div class="footer__inner">
          <span><span aria-hidden="true">✓</span>${this.t('localFooter')}</span>
          <a href="${REPOSITORY_URL}" target="_blank" rel="noreferrer">${this.t('sourceFooter')}</a>
        </div>
      </footer>
    `;
  }

  private bind(): void {
    this.root
      .querySelector<HTMLButtonElement>('[data-action="theme"]')
      ?.addEventListener('click', () => this.toggleTheme());
    this.root
      .querySelector<HTMLButtonElement>('[data-action="locale"]')
      ?.addEventListener('click', () => this.toggleLocale());
    bindDropZone(this.root, {
      translate: this.t,
      disabled: this.records.length >= MAX_BATCH_SIZE,
      onFiles: (files) => this.addFiles(files),
    });
    bindResultTable(this.root, this.resultTableOptions());
    this.detailCleanup = bindDetailPanel(this.root, this.detailState, this.detailPanelOptions());
    bindToasts(this.root, { translate: this.t, onDismiss: (id) => this.dismissToast(id) });
  }

  private resultTableOptions() {
    return {
      translate: this.t,
      onSearch: (value: string) => {
        this.search = value;
        this.render();
      },
      onFilter: (value: ResultFilter) => {
        this.filter = value;
        this.render();
      },
      onSort: (value: ResultSort) => {
        this.sort = value;
        this.render();
      },
      onToggleAll: (checked: boolean) => this.toggleAllVisible(checked),
      onToggleRecord: (id: string, checked: boolean) => this.toggleRecord(id, checked),
      onOpen: (id: string) => this.openRecord(id),
      onDelete: (id: string) => this.deleteRecords([id]),
      onReanalyze: (id: string) => this.reanalyze([id]),
      onDeleteSelected: () => this.deleteRecords(Array.from(this.selectedIds)),
      onReanalyzeSelected: () => this.reanalyze(Array.from(this.selectedIds)),
      onClear: () => this.clearAll(),
      onExportJson: () => this.exportReport('json'),
      onExportCsv: () => this.exportReport('csv'),
    };
  }

  private detailPanelOptions() {
    return {
      translate: this.t,
      locale: this.locale,
      onBackground: (background: PreviewBackground) => {
        this.detailState.preview.background = background;
        this.render();
      },
      onZoomMode: (mode: ZoomMode) => {
        this.detailState.preview.zoomMode = mode;
        this.render();
      },
      onZoom: (scale: number) => {
        this.detailState.preview.zoomMode = 'custom' as const;
        this.detailState.preview.customScale = scale;
        this.render();
      },
      onBoundingBox: (visible: boolean) => {
        this.detailState.preview.showBoundingBox = visible;
        this.render();
      },
      onMarginGuides: (visible: boolean) => {
        this.detailState.preview.showMarginGuides = visible;
        this.render();
      },
      onTrimMargin: (margin: 0 | 2 | 4 | 8 | 16) => {
        this.detailState.trimMargin = margin;
        this.render();
      },
      onTrim: (id: string) => void this.trimRecord(id),
      onReanalyze: (id: string) => this.reanalyze([id]),
    };
  }

  private addFiles(files: File[]): void {
    if (files.length === 0) {
      this.addToast(this.t('validationNothingAdded'), 'warning');
      return;
    }
    const fingerprints = new Set(this.records.map((record) => record.fingerprint));
    const available = Math.max(0, MAX_BATCH_SIZE - this.records.length);
    const accepted: File[] = [];
    let exceededBatchLimit = false;

    for (const file of files) {
      if (!isPngFile(file)) {
        this.addToast(this.t('validationInvalidType', { name: file.name }), 'error', false);
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        this.addToast(this.t('validationTooLarge', { name: file.name }), 'error', false);
        continue;
      }
      const fingerprint = fileFingerprint(file);
      if (fingerprints.has(fingerprint)) {
        this.addToast(this.t('validationDuplicate', { name: file.name }), 'warning', false);
        continue;
      }
      if (accepted.length >= available) {
        exceededBatchLimit = true;
        continue;
      }
      fingerprints.add(fingerprint);
      accepted.push(file);
    }

    if (exceededBatchLimit) {
      this.addToast(this.t('validationBatchLimit', { count: available }), 'warning', false);
    }

    if (accepted.length === 0) {
      this.render();
      return;
    }
    const newRecords = accepted.map<UiRecord>((file, index) => ({
      id: makeId(),
      file,
      fingerprint: fileFingerprint(file),
      objectUrl: URL.createObjectURL(file),
      status: 'queued',
      progress: 0,
      addedAt: Date.now() + index,
    }));
    this.records.push(...newRecords);
    this.analysisQueue.push(...newRecords.map((record) => record.id));
    this.activeId ??= newRecords[0]?.id;
    this.addToast(this.t('addedFiles', { count: newRecords.length }), 'success', false);
    this.render();
    this.pumpAnalysisQueue();
  }

  private pumpAnalysisQueue(): void {
    while (this.activeAnalyses < ANALYSIS_CONCURRENCY && this.analysisQueue.length > 0) {
      const id = this.analysisQueue.shift();
      const record = this.records.find((candidate) => candidate.id === id);
      if (!record || record.status !== 'queued') continue;
      this.activeAnalyses += 1;
      void this.analyzeRecord(record).finally(() => {
        this.activeAnalyses -= 1;
        this.pumpAnalysisQueue();
      });
    }
  }

  private async analyzeRecord(record: UiRecord): Promise<void> {
    record.status = 'analyzing';
    record.progress = 0;
    record.error = undefined;
    record.analysis = undefined;
    this.scheduleRender();
    try {
      const result = await analyzeFile(record.file, (rawProgress) => {
        if (!this.records.includes(record)) return;
        const progress = rawProgress <= 1 ? rawProgress * 100 : rawProgress;
        record.progress = Math.max(record.progress, Math.min(99, progress));
        this.scheduleRender();
      });
      if (!this.records.includes(record)) return;
      record.analysis = result;
      record.progress = 100;
      record.status = 'ready';
      this.render();
    } catch (error) {
      if (!this.records.includes(record)) return;
      record.status = 'error';
      record.error = error instanceof Error ? error.message : String(error);
      record.progress = 0;
      this.addToast(`${record.file.name}: ${record.error}`, 'error', false);
      this.render();
    }
  }

  private scheduleRender(): void {
    if (this.renderFrame !== undefined) return;
    this.renderFrame = requestAnimationFrame(() => {
      this.renderFrame = undefined;
      this.render();
    });
  }

  private getVisibleRecords(): UiRecord[] {
    const query = this.search.trim().toLocaleLowerCase();
    const filtered = this.records.filter((record) => {
      if (query && !record.file.name.toLocaleLowerCase().includes(query)) return false;
      if (this.filter === 'all') return true;
      if (this.filter === 'ready') return record.status === 'ready';
      if (this.filter === 'progress')
        return record.status === 'queued' || record.status === 'analyzing';
      if (this.filter === 'error') return record.status === 'error';
      if (record.status !== 'ready') return false;
      const analysis = normalizeAnalysis(record);
      if (this.filter === 'transparent') return analysis.realTransparency;
      if (this.filter === 'opaque') return analysis.fullyOpaque;
      return analysis.suspected;
    });

    return filtered.sort((left, right) => {
      if (this.sort === 'name') return left.file.name.localeCompare(right.file.name, this.locale);
      if (this.sort === 'transparent-desc' || this.sort === 'transparent-asc') {
        const leftValue =
          left.status === 'ready' ? normalizeAnalysis(left).transparentPercentage : -1;
        const rightValue =
          right.status === 'ready' ? normalizeAnalysis(right).transparentPercentage : -1;
        return this.sort === 'transparent-desc' ? rightValue - leftValue : leftValue - rightValue;
      }
      return left.addedAt - right.addedAt;
    });
  }

  private toggleAllVisible(checked: boolean): void {
    for (const record of this.getVisibleRecords()) {
      if (checked) this.selectedIds.add(record.id);
      else this.selectedIds.delete(record.id);
    }
    this.render();
  }

  private toggleRecord(id: string, checked: boolean): void {
    if (checked) this.selectedIds.add(id);
    else this.selectedIds.delete(id);
    this.render();
  }

  private openRecord(id: string): void {
    if (!this.records.some((record) => record.id === id)) return;
    this.activeId = id;
    this.detailState.preview.zoomMode = 'fit';
    this.render();
    if (window.matchMedia('(max-width: 760px)').matches) {
      this.root
        .querySelector('[data-testid="detail-panel"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private deleteRecords(ids: string[]): void {
    const idSet = new Set(ids);
    for (const record of this.records) {
      if (idSet.has(record.id)) URL.revokeObjectURL(record.objectUrl);
    }
    this.records = this.records.filter((record) => !idSet.has(record.id));
    this.analysisQueue = this.analysisQueue.filter((id) => !idSet.has(id));
    for (const id of ids) this.selectedIds.delete(id);
    if (this.activeId && idSet.has(this.activeId)) this.activeId = this.records[0]?.id;
    this.render();
  }

  private clearAll(): void {
    for (const record of this.records) URL.revokeObjectURL(record.objectUrl);
    this.records = [];
    this.analysisQueue = [];
    this.selectedIds.clear();
    this.activeId = undefined;
    this.render();
  }

  private reanalyze(ids: string[]): void {
    const queued: string[] = [];
    for (const record of this.records) {
      if (!ids.includes(record.id) || record.status === 'analyzing') continue;
      record.status = 'queued';
      record.progress = 0;
      record.analysis = undefined;
      record.error = undefined;
      queued.push(record.id);
    }
    this.analysisQueue.push(...queued);
    this.render();
    this.pumpAnalysisQueue();
  }

  private exportReport(format: 'json' | 'csv'): void {
    const readyRecords = this.records.filter((record) => record.status === 'ready');
    if (readyRecords.length === 0) {
      this.addToast(this.t('exportNothing'), 'warning');
      return;
    }
    const date = new Date().toISOString().slice(0, 10);
    const results = readyRecords.flatMap((record) => (record.analysis ? [record.analysis] : []));
    if (format === 'json') {
      triggerDownload(
        new Blob([exportReportJson(results)], { type: 'application/json;charset=utf-8' }),
        `truealpha-report-${date}.json`,
      );
    } else {
      triggerDownload(
        new Blob(['\uFEFF', exportReportCsv(results)], { type: 'text/csv;charset=utf-8' }),
        `truealpha-report-${date}.csv`,
      );
    }
  }

  private async trimRecord(id: string): Promise<void> {
    const record = this.records.find((candidate) => candidate.id === id);
    if (!record?.analysis || !normalizeAnalysis(record).safeToTrim || this.trimmingId) return;
    this.trimmingId = id;
    this.render();
    try {
      const output = await trimFile(
        { file: record.file, analysis: record.analysis },
        this.detailState.trimMargin,
      );
      const blob = extractBlob(output);
      if (!blob) throw new Error(this.t('trimError'));
      triggerDownload(blob, trimmedFileName(record.file.name));
      this.addToast(
        this.t('trimSuccess', { margin: this.detailState.trimMargin }),
        'success',
        false,
      );
    } catch (error) {
      this.addToast(error instanceof Error ? error.message : this.t('trimError'), 'error', false);
    } finally {
      this.trimmingId = undefined;
      this.render();
    }
  }

  private addToast(message: string, tone: ToastTone, shouldRender = true): void {
    const toast: ToastMessage = { id: this.nextToastId, message, tone };
    this.nextToastId += 1;
    this.toasts = [...this.toasts.slice(-3), toast];
    window.setTimeout(() => this.dismissToast(toast.id), 7000);
    if (shouldRender) this.render();
  }

  private dismissToast(id: number): void {
    const next = this.toasts.filter((toast) => toast.id !== id);
    if (next.length === this.toasts.length) return;
    this.toasts = next;
    this.render();
  }
}

function trustedRepositoryUrl(value: string | undefined): string {
  if (!value) return DEFAULT_REPOSITORY_URL;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'github.com'
      ? url.href
      : DEFAULT_REPOSITORY_URL;
  } catch {
    return DEFAULT_REPOSITORY_URL;
  }
}

function makeId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
